import React, { useState } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Alert, CircularProgress,
  Divider, Stack, alpha, useTheme,
} from '@mui/material';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  getMultiFactorResolver,
  MultiFactorError,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { SoftChip } from '../components/SoftChip';

// SVG logo Google (riprodotto inline per non aggiungere dipendenze)
const GoogleLogo: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
    <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05" />
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335" />
  </svg>
);

export const LoginPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goAfterLogin = () => {
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/tenants';
    navigate(from, { replace: true });
  };

  const handleAuthError = (err: unknown): boolean => {
    const fbErr = err as { code?: string };
    if (fbErr.code === 'auth/multi-factor-auth-required') {
      const resolver = getMultiFactorResolver(auth, err as MultiFactorError);
      navigate('/mfa-verify', { state: { resolver } });
      return true;
    }
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      goAfterLogin();
    } catch (err) {
      if (handleAuthError(err)) return;
      console.error('Login failed', err);
      if (err instanceof FirebaseError && err.code.startsWith('auth/')) {
        setError('Credenziali non valide');
      } else {
        setError('Errore di connessione, riprova');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await signInWithPopup(auth, provider);
      goAfterLogin();
    } catch (err) {
      if (handleAuthError(err)) return;
      const fbErr = err as { code?: string };
      console.error('Google sign-in failed', err);
      if (fbErr.code === 'auth/popup-blocked') {
        try {
          await signInWithRedirect(auth, provider);
          return;
        } catch {
          setError('Apertura popup bloccata. Controlla le impostazioni del browser.');
        }
      } else if (fbErr.code === 'auth/popup-closed-by-user' || fbErr.code === 'auth/cancelled-popup-request') {
        return;
      } else {
        setError('Accesso Google non riuscito. Riprova.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        // Sfondo radial leggero in slate per richiamare l'AppBar platform
        backgroundImage: `radial-gradient(circle at 50% 0%, ${alpha('#0f172a', 0.08)} 0%, transparent 60%)`,
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          maxWidth: 420,
          width: '100%',
          borderRadius: 3,
        }}
      >
        {/* Brand mark — coerente con quello in AppBar */}
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.25,
              bgcolor: '#0f172a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: '#fff',
              letterSpacing: '0.04em',
            }}
          >
            PW
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              Operator Console
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Preventivatore Web
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ mb: 2.5, flexWrap: 'wrap', rowGap: 0.5 }}>
          <SoftChip label="PLATFORM" tone="warning" />
          <SoftChip label="accesso riservato" tone="default" />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Superficie operatore della piattaforma. Se sei un cliente di un tenant,
          accedi dalla tua app gestionale.
        </Typography>

        {/* Google sign-in (consigliato) */}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={handleGoogleSignIn}
          disabled={loading || googleLoading}
          startIcon={googleLoading ? <CircularProgress size={18} /> : <GoogleLogo />}
          sx={{
            borderColor: 'divider',
            color: 'text.primary',
            textTransform: 'none',
            fontWeight: 500,
            py: 1.2,
            '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
          }}
        >
          {googleLoading ? 'Accesso in corso...' : 'Accedi con Google'}
        </Button>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2.5 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            oppure
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Stack>

        {/* Email + password */}
        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            size="small"
            margin="normal"
            disabled={loading || googleLoading}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            size="small"
            margin="normal"
            disabled={loading || googleLoading}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || googleLoading}
            sx={{ mt: 3, py: 1.1 }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {loading ? 'Accesso...' : 'Accedi'}
          </Button>
        </form>

        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', mt: 3, textAlign: 'center', fontSize: '0.7rem' }}
        >
          Accessi tracciati nell'audit log della piattaforma · {theme.palette.mode === 'light' ? 'v1.0' : ''}
        </Typography>
      </Paper>
    </Box>
  );
};
